<template>
  <DialogLayout :class-name="'update-password-dialog'" @close="handleClose">
    <template #title>{{ $t('account.updatePasswordDialog.title') }}</template>
    <template #default>
      <el-form ref="formRef" :model="formData" :rules="rules" label-position="top">
        <el-form-item :label="$t('account.updatePasswordDialog.oldPassword')" prop="oldPassword" required>
          <el-input
            v-model="formData.oldPassword"
            type="password"
            :placeholder="$t('account.updatePasswordDialog.inputPlaceholder')"
            show-password
          />
        </el-form-item>

        <el-form-item :label="$t('account.updatePasswordDialog.newPassword')" prop="newPassword" required>
          <el-input
            v-model="formData.newPassword"
            type="password"
            :placeholder="$t('account.updatePasswordDialog.newPasswordPlaceholder')"
            show-password
          />
        </el-form-item>

        <el-form-item :label="$t('account.updatePasswordDialog.confirmPassword')" prop="confirmPassword" required>
          <el-input
            v-model="formData.confirmPassword"
            type="password"
            :placeholder="$t('account.updatePasswordDialog.newPasswordPlaceholder')"
            show-password
          />
        </el-form-item>
      </el-form>
    </template>
    <template #footer>
      <el-button type="default" class="" @click="handleCancel">
        {{ $t('account.updatePasswordDialog.cancel') }}
      </el-button>
      <el-button type="primary" @click="handleConfirm">
        {{ $t('account.updatePasswordDialog.confirm') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElMessage, type FormInstance, type FormRules } from 'element-plus';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import {
  updateProfilePassword
} from '@/api/account'
interface Props {
  initialOldPassword?: string;
}

const props = withDefaults(defineProps<Props>(), {
  initialOldPassword: ''
});

const emit = defineEmits<{
  cancel: [];
  confirm: [data: { oldPassword: string; newPassword: string }];
  close: [];
}>();

const { t } = useI18n();
const formRef = ref<FormInstance>();

const formData = reactive({
  oldPassword: props.initialOldPassword,
  newPassword: '',
  confirmPassword: ''
});

const validatePassword = (rule: any, value: any, callback: any) => {
  if (value === '') {
    callback(new Error(t('account.updatePasswordDialog.validation.newPasswordRequired')));
  } else if (value.length < 8) {
    callback(new Error(t('account.updatePasswordDialog.validation.passwordLength')));
  } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(value)) {
    callback(new Error(t('account.updatePasswordDialog.validation.passwordFormat')));
  } else {
    callback();
  }
};

const validateConfirmPassword = (rule: any, value: any, callback: any) => {
  if (value === '') {
    callback(new Error(t('account.updatePasswordDialog.validation.confirmPasswordRequired')));
  } else if (value !== formData.newPassword) {
    callback(new Error(t('account.updatePasswordDialog.validation.passwordMismatch')));
  } else {
    callback();
  }
};

const rules = reactive<FormRules>({
  oldPassword: [
    { required: true, message: t('account.updatePasswordDialog.validation.oldPasswordRequired'), trigger: 'blur' }
  ],
  newPassword: [
    { required: true, validator: validatePassword, trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, validator: validateConfirmPassword, trigger: 'blur' }
  ]
});

const handleCancel = () => {
  emit('cancel');
};

const handleConfirm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate((valid) => {
    if (valid) {
      updateProfilePassword({
        currentPassword: formData.oldPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      }).then(() => {
        ElMessage.success(t('account.updatePasswordDialog.successMessage'))
        emit('confirm', {
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        });
      }).catch(err => {
        ElMessage.error(err.message);
      })
    }
  });
};

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
.update-password-dialog {
  min-width: 592px;
  max-width: 592px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  :deep(.dialog-footer) {
    padding-top: 0;
  }
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}
.el-form > .el-form-item:last-child {
  /* 你的样式 */
  // margin-bottom: 0;
}

:deep(.el-form-item__label) {
  font-size: 14px;
  color: #262626;
  font-weight: 500;
  margin-bottom: 8px;
}

:deep(.el-input__inner) {
  font-size: 14px;
}
</style>
